/* eslint-disable class-methods-use-this */
import Nes from "@hapi/nes/lib/client";
import store from "@/store";
import config from "./config";

class SocketConnection {
  client;

  cb;

  isConnected = false;

  isConnecting = false;

  subscriptions = new Map();

  activeChannels = new Set();

  reconnectTimeout = null;

  constructor() {
    // Don't auto-connect - use lazy connection only when needed
    // This prevents connection when QR code opens
    // eslint-disable-next-line no-console
    console.log(
      "[WS] SocketConnection initialized - connection will be created when needed"
    );
  }

  async connect(serverUrl = null, authOptions = null) {
    if (this.isConnected || this.isConnecting) {
      return this.client;
    }

    const url = serverUrl || config?.ws?.url;
    if (!url) {
      // eslint-disable-next-line no-console
      console.warn("[WS] WebSocket URL is not configured.");
      return null;
    }

    // Validate URL format
    if (!url.startsWith("ws://") && !url.startsWith("wss://")) {
      // eslint-disable-next-line no-console
      console.error(
        "[WS] Invalid WebSocket URL. Must start with ws:// or wss://"
      );
      return null;
    }

    this.isConnecting = true;
    // eslint-disable-next-line no-console
    console.log("[WS] Connecting to WebSocket server:", url);

    return new Promise((resolve, reject) => {
      try {
        // Disconnect existing connection if any
        if (this.client) {
          this.disconnect();
        }

        this.client = new Nes.Client(url);
        this.isConnected = false;

        const connectOptions = authOptions || {
          auth: {
            headers: { Authorization: config.getWSAuth() },
          },
        };

        let connectionResolved = false;
        let checkInterval = null;
        let timeoutId = null;

        // Connection check function
        const checkConnection = () => {
          if (connectionResolved) return false;

          const hasClient = !!this.client;
          const hasClientId = this.client?.id != null;
          const socketReady =
            this.client?._socket?.readyState === 1 ||
            this.client?.socket?.readyState === 1;

          if (hasClient && (hasClientId || socketReady)) {
            // eslint-disable-next-line no-console
            console.log("[WS] Connection detected!", {
              hasClientId,
              socketReady,
              clientId: this.client?.id,
            });
            this.isConnected = true;
            this.isConnecting = false;
            connectionResolved = true;
            if (checkInterval) clearInterval(checkInterval);
            if (timeoutId) clearTimeout(timeoutId);
            this.client.onDisconnect = this.handleDisconnect;
            this.client.onError = this.handleError;
            this.resubscribeActiveChannels()
              .then(() => {
                resolve(this.client);
              })
              .catch(reject);
            return true;
          }
          return false;
        };

        // Check connection status every 200ms
        checkInterval = setInterval(() => {
          checkConnection();
        }, 200);

        // Timeout after 5 seconds
        timeoutId = setTimeout(() => {
          if (!connectionResolved) {
            clearInterval(checkInterval);
            connectionResolved = true;
            this.isConnected = false;
            this.isConnecting = false;
            // eslint-disable-next-line no-console
            console.error("[WS] Connection timeout");
            reject(
              new Error(
                "Connection timeout: Unable to establish connection within 5 seconds"
              )
            );
          }
        }, 5000);

        // Primary connection method - callback
        this.client.connect(connectOptions, (err) => {
          if (connectionResolved) return;

          clearInterval(checkInterval);
          clearTimeout(timeoutId);
          connectionResolved = true;

          if (err) {
            // eslint-disable-next-line no-console
            console.error("[WS] Socket connection error:", err);
            this.isConnected = false;
            this.isConnecting = false;
            reject(err);
            return;
          }

          // eslint-disable-next-line no-console
          console.log("[WS] Socket connected via callback");
          this.isConnected = true;
          this.isConnecting = false;
          this.client.onDisconnect = this.handleDisconnect;
          this.client.onError = this.handleError;
          this.resubscribeActiveChannels()
            .then(() => {
              resolve(this.client);
            })
            .catch(reject);
        });
      } catch (error) {
        this.isConnecting = false;
        // eslint-disable-next-line no-console
        console.error("[WS] Error creating socket client:", error);
        reject(error);
      }
    });
  }

  scheduleReconnect() {
    if (this.reconnectTimeout) {
      return;
    }
    this.reconnectTimeout = setTimeout(() => {
      this.reconnectTimeout = null;
      this.connect();
    }, 5000);
  }

  async resubscribeActiveChannels() {
    if (!this.client || !this.activeChannels.size) {
      return;
    }
    // Note: We need to store callbacks separately if we want to resubscribe
    // For now, just log that resubscription would happen
    // The subscriptions will be recreated when subscribe methods are called
    // eslint-disable-next-line no-console
    console.log(
      "[WS] Active channels to resubscribe:",
      Array.from(this.activeChannels)
    );
    // Clear subscriptions since we'll need to resubscribe
    // The component will re-subscribe when needed via subscribePaymentStatusUpdate
    this.subscriptions.clear();
  }

  handleDisconnect = (willReconnect, log) => {
    this.isConnected = false;
    if (!willReconnect) {
      // eslint-disable-next-line no-console
      console.warn("[WS] Disconnected", log);
      this.scheduleReconnect();
    }
  };

  handleError = (err) => {
    // eslint-disable-next-line no-console
    console.error("[WS] Client error", err);
  };

  handleMessage = (channel, message, flags) => {
    const payload = { channel, message, flags };
    store.commit("user/setSocketResponse", message);
    if (typeof this.cb === "function") {
      this.cb(payload);
    }
  };

  async ensureConnected() {
    if (!this.connected() && !this.isConnecting) {
      await this.connect();
    }
    // Wait for connection if connecting
    if (this.isConnecting) {
      let attempts = 0;
      while (this.isConnecting && attempts < 50) {
        await new Promise((resolve) => setTimeout(resolve, 100));
        attempts++;
      }
    }
    return this.connected();
  }

  async subscribe(channel, callback) {
    if (!channel) {
      return Promise.reject(new Error("Channel is required"));
    }

    // Ensure connected first
    const connected = await this.ensureConnected();
    if (!connected || !this.client) {
      return Promise.reject(new Error("Not connected to WebSocket server"));
    }

    const alreadySubscribed = this.subscriptions.has(channel);
    if (alreadySubscribed) {
      // eslint-disable-next-line no-console
      console.log(`[WS] Already subscribed to ${channel}`);
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      try {
        // eslint-disable-next-line no-console
        console.log(`[WS] Subscribing to ${channel}...`);
        // eslint-disable-next-line no-console
        console.log("[WS] Client ID:", this.client.id);

        const handler = (message, flags) => {
          this.handleMessage(channel, message, flags);
          // If callback provided, call it
          if (callback && typeof callback === "function") {
            callback(message);
          }
        };

        this.client.subscribe(channel, handler, (err) => {
          if (err) {
            // eslint-disable-next-line no-console
            console.error(`[WS] Subscription error for ${channel}:`, err);
            reject(err);
          } else {
            // eslint-disable-next-line no-console
            console.log(`[WS] Successfully subscribed to ${channel}`);
            this.activeChannels.add(channel);
            this.subscriptions.set(channel, handler);
            resolve();
          }
        });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(`[WS] Failed to subscribe ${channel}`, error);
        reject(error);
      }
    });
  }

  async unsubscribe(channel) {
    if (!channel) {
      return;
    }

    if (!this.client) {
      this.activeChannels.delete(channel);
      this.subscriptions.delete(channel);
      return;
    }

    const handler = this.subscriptions.get(channel);
    if (!handler) {
      this.activeChannels.delete(channel);
      return;
    }

    try {
      // Unsubscribe using the handler (subscription reference)
      await this.client.unsubscribe(handler);
      // eslint-disable-next-line no-console
      console.log(`[WS] Unsubscribed from ${channel}`);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn(`[WS] Failed to unsubscribe ${channel}`, error);
      // Fallback: try channel name if handler unsubscribe fails
      try {
        await this.client.unsubscribe(channel);
      } catch (fallbackError) {
        // eslint-disable-next-line no-console
        console.warn(
          `[WS] Fallback unsubscribe also failed for ${channel}`,
          fallbackError
        );
      }
    } finally {
      this.activeChannels.delete(channel);
      this.subscriptions.delete(channel);
    }
  }

  async subscribePaymentStatusUpdate(callback) {
    const channel = "/payin-payment-status-update";
    return this.subscribe(channel, callback);
  }

  async unsubscribePaymentStatusUpdate() {
    const channel = "/payin-payment-status-update";
    await this.unsubscribe(channel);
  }

  async unsubscribeAll() {
    const channels = Array.from(this.activeChannels);
    await Promise.all(channels.map((channel) => this.unsubscribe(channel)));
  }

  connected() {
    // Check multiple indicators of connection
    const hasClient = !!this.client;
    const hasClientId = this.client?.id != null;
    const socketReady =
      this.client?._socket?.readyState === 1 ||
      this.client?.socket?.readyState === 1;
    return this.isConnected && hasClient && (hasClientId || socketReady);
  }

  getConnectionStatus() {
    return this.connected() && this.client;
  }

  onEventReceived(func) {
    this.cb = func;
  }

  offEventReceived() {
    this.cb = null;
  }

  async disconnect() {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    await this.unsubscribeAll();

    if (this.client && this.isConnected) {
      try {
        await this.client.disconnect();
      } catch (error) {
        // eslint-disable-next-line no-console
        console.warn("[WS] Error while disconnecting", error);
      }
    }

    this.isConnected = false;
    this.client = null;
  }
}

const websocket = new SocketConnection();
export default websocket;
